import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import RootLayout from './layouts/RootLayout';
import SignUpLayout from './layouts/SignUpLayout';
import SomethingWentWrong from './pages/SomethingWentWrong';
import SiteUnavailable from './pages/SiteUnavailable';
import ExpiredTokenError from './pages/ExpiredTokenError';
import SomethingWentWrongTeacherSignUpPage from './pages/signup/SomethingWentWrongTeacherSignUp';
import StoreProvider from './redux/StoreProvider';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Invite = lazy(() => import('./pages/Invite'));
const RecoverPassword = lazy(() => import('./pages/RecoverPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SuccessRecoverPassword = lazy(
  () => import('./pages/SuccessRecoverPassword'),
);
const SuccessResetPassword = lazy(() => import('./pages/SuccessResetPassword'));
const SessionExpiredError = lazy(() => import('./pages/SessionExpiredError'));
const HasNoSeatsError = lazy(() => import('./pages/HasNoSeatsError'));
const SignUpAccount = lazy(() => import('./pages/signup/SignUpAccount'));
const SignUpUser = lazy(() => import('./pages/signup/SignUpUser'));
const SignUpEmailVerification = lazy(
  () => import('./pages/signup/SignUpEmailVerification'),
);
const SignUpVerifyEmail = lazy(
  () => import('./pages/signup/SignUpVerifyEmail'),
);
const SignUpClaim = lazy(() => import('./pages/signup/SignUpClaim'));
const SignUpSuccessAccountCreated = lazy(
  () => import('./pages/signup/SignUpSuccessAccountCreated'),
);

function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route element={<RootLayout />}>
          <Route
            index
            element={<Home />}
          />
          <Route
            path="login"
            element={<Login />}
          />
          <Route
            path="invite"
            element={<Invite />}
          />
          <Route
            path="session-expired-error"
            element={<SessionExpiredError />}
          />
          <Route
            path="something-went-wrong-error"
            element={<SomethingWentWrong />}
          />
          <Route
            path="site-unavailable"
            element={<SiteUnavailable />}
          />
          <Route
            path="expired-token-error"
            element={<ExpiredTokenError />}
          />
          <Route
            path="has-no-seats-error"
            element={<HasNoSeatsError />}
          />

          <Route
            path="recover-password"
            element={<RecoverPassword />}
          />
          <Route
            path="reset-password"
            element={<ResetPassword />}
          />
          <Route
            path="success-recover-password"
            element={<SuccessRecoverPassword />}
          />
          <Route
            path="success-reset-password"
            element={<SuccessResetPassword />}
          />
          <Route
            path="signup/account"
            element={<SignUpAccount />}
          />
          <Route
            path="signup/claim/:code"
            element={<SignUpClaim />}
          />
          <Route
            path="signup/user"
            element={<SignUpUser />}
          />
          <Route
            path="signup/email-verification"
            element={<SignUpEmailVerification />}
          />
          <Route
            path="signup/verify-email"
            element={<SignUpVerifyEmail />}
          />
          <Route
            path="signup/success-account-created"
            element={<SignUpSuccessAccountCreated />}
          />
          <Route
            path="signup/something-went-wrong-teacher-sign-up"
            element={<SomethingWentWrongTeacherSignUpPage />}
          />

          {/* 404 SPA */}
          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
              />
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
}

const SentryFallback = () => (
  <StoreProvider>
    <SomethingWentWrong />
  </StoreProvider>
);

// Wrap the app with Sentry Error Boundary
export default Sentry.withErrorBoundary(App, {
  fallback: () => <SentryFallback />,
  showDialog: true,
});
